import { useSearchParams } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { localDateFromString, localDateToString, getStartOfWeek, getEndOfWeek, getWeekDays } from '@/utils/datetime';
import { setLastViewedDate as defaultSetLastViewedDate } from '../meal-plan/state';

export function useDateSelection({ 
  weekStartDay = 1, 
  stateKey = 'mealPlan',
  setLastViewedDateAction = defaultSetLastViewedDate,
  isReady = true
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const lastViewedDate = useSelector((state) => state[stateKey]?.lastViewedDate);
  
  const dateParam = searchParams.get('date');
  
  useEffect(() => {
    if (dateParam && isReady) {
      dispatch(setLastViewedDateAction(dateParam));
    }
  }, [dateParam, isReady, dispatch, setLastViewedDateAction]);

  useEffect(() => {
    if (!dateParam && isReady) {
      const dateToUse = lastViewedDate || localDateToString(new Date());
      setSearchParams({ date: dateToUse }, { replace: true });
    }
  }, [dateParam, setSearchParams, isReady, lastViewedDate]);

  const selectedDate = useMemo(() => {
    if (dateParam) {
      return localDateFromString(dateParam);
    }
    return new Date();
  }, [dateParam]);

  const selectedDateString = useMemo(() => {
    return localDateToString(selectedDate);
  }, [selectedDate]);

  const currentWeek = useMemo(() => {
    return getStartOfWeek(selectedDate, weekStartDay);
  }, [selectedDate, weekStartDay]);

  const endOfWeek = useMemo(() => {
    return getEndOfWeek(currentWeek);
  }, [currentWeek]);

  const weekDays = useMemo(() => {
    return getWeekDays(currentWeek);
  }, [currentWeek]);

  const onDateChange = (date, { replace = false } = {}) => {
    const dateString = localDateToString(date);
    dispatch(setLastViewedDateAction(dateString));
    setSearchParams({ date: dateString }, { replace });
  };

  return {
    selectedDate,
    selectedDateString,
    currentWeek,
    endOfWeek,
    weekDays,
    weekStartDay,
    onDateChange,
  };
}

